import invariant from 'invariant';
import difference from 'lodash/difference';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';

// Test IDs for Playwright tests.
export enum TestIds {
  // #region Supply chain
  SupplierTable = 'supplier-table',
  SupplierTableSearchField = 'supplier-table-search-field',
  SuppliersTableSearchFieldExpandButton = 'supplier-table-search-field-expand-button',
  SupplierFilterPopover = 'supplier-filter-popover',
  SupplierFilterPopoverButton = 'supplier-filter-popover-button',
  SupplierTableFilterForm = 'supplier-filter-form',
  SupplierFilterAddButton = 'supplier-filter-add-button',
  SupplierFilterDeleteButton = 'supplier-filter-delete-button',
  SupplierFilterSubmitButton = 'supplier-filter-submit-buttom',
  SupplierTableMapButton = 'supplier-table-map-button',
  SupplierMapDialog = 'supplier-map-dialog',
  SupplierDetailsPageSidebar = 'supplier-details-page-sidebar',
  SupplierDetailsPageSidebarEmissions = 'supplier-details-page-sidebar-emissions',
  SupplierDetailsPageSidebarSpend = 'supplier-details-page-sidebar-spend',
  SupplierDetailsPageSidebarEditContact = 'supplier-details-page-sidebar-edit-contact',
  SupplierDetailsPageOverviewTab = 'supplier-details-page-overview-tab',
  SupplierDetailsPageDisclosuresTab = 'supplier-details-page-disclosures-tab',
  SupplierDetailsPageSupplierEfsTab = 'supplier-details-page-supplier-efs-tab',
  EmissionsFactorGrid = 'emissions-factors-grid',
  SupplierDetailsPageDisclosureSection = 'supplier-details-page-disclosure-section',
  SupplierDetailsPageDisclosureTimelineItem = 'supplier-details-page-disclosure-timeline-item',
  SupplierDetailsPageContributionSection = 'supplier-details-page-contribution-section',
  SupplierDetailsPageEmissionsSection = 'supplier-details-page-emissions-section',
  SupplierDetailsPageTargetsSection = 'supplier-details-page-targets-section',
  SupplierDetailsPageTargetItem = 'supplier-details-page-target-item',
  SupplierDetailsPageCommitmentsSection = 'supplier-details-page-commitments-section',
  SupplierDetailsPageCommitmentItem = 'supplier-details-page-commitment-item',
  SupplierDetailsPageCombinedTargetsSection = 'supplier-details-page-combined-targets-section',
  SupplierDetailsPageAddTargetButton = 'supplier-details-page-add-target-button',
  SupplierDetailsPageInitiativesSection = 'supplier-details-page-initiatives-section',
  SupplierDetailsPageCreateTask = 'supplier-details-page-create-task',
  SupplierDetailsPageNewContactForm = 'supplier-details-page-new-contact-form',
  SupplierDetailsPageNewContactButton = 'supplier-details-page-new-contact-button',
  SupplierDetailsPageNewContactNameField = 'supplier-details-page-new-contact-name-field',
  SupplierDetailsPageNewContactEmailField = 'supplier-details-page-new-contact-email-field',
  SupplierDetailsPagePortalCreationRemoveTaskButton = 'supplier-details-page-portal-creation-remove-task-button',
  SupplierDetailsPageCloseSurveyButton = 'supplier-details-page-close-survey-button',
  SupplierDetailsPageEmailTemplateDialog = 'supplier-details-page-email-template-dialog',
  SupplierDetailsPageEngagementSection = 'supplier-details-page-engagement-section',
  SupplierDetailsPageResetFiltersButton = 'supplier-details-page-reset-filters-button',
  SupplierDetailsPageTasksTableElement = 'supplier-details-page-tasks-table',
  SupplierEmissionsTargetChart = 'supplier-emissions-target-chart',
  SupplierPriorityChip = 'supplier-priority-chip',
  ProgramConfigurationContainer = 'program-configuration-container',
  ProgramConfigurationButton = 'program-configuration-button',
  IncludedGhgCategories = 'included-ghg-categories',
  DisclosureQualityMessage = 'disclosure-quality-msg',
  SurveyActions = 'survey-actions',
  SurveySendButton = 'survey-send-button',
  SupplierMatchingTable = 'supplier-matching-table',
  SupplierMatchingTableToolbarLabel = 'supplier-matching-table-toolbar-label',
  SuppliersPageTabOverview = 'suppliers-page-tab-overview',
  SuppliersPageTabTasks = 'suppliers-page-tab-tasks',
  SuppliersPageToolbarLabel = 'suppliers-page-toolbar-label',
  SuppliersConfigTabOverview = 'suppliers-config-tab-overview',
  SuppliersConfigTabMaturityScoring = 'suppliers-config-tab-maturity-scoring',
  SuppliersConfigTabPortal = 'suppliers-config-tab-portal',
  SuppliersConfigTabCohorts = 'suppliers-config-tab-cohorts',
  SuppliersConfigTabSupplierMatching = 'suppliers-config-tab-supplier-matching',
  SuppliersConfigTabTasks = 'suppliers-config-tab-tasks',
  SuppliersConfigTabDeploys = 'suppliers-config-tab-deploys',
  SuppliersConfigTabMetrics = 'suppliers-config-tab-metrics',
  SuppliersConfigTabViews = 'suppliers-config-tab-views',
  SuppliersConfigTabNotifications = 'suppliers-config-tab-notifications',
  SuppliersConfigTabCustomSuppliers = 'suppliers-config-tab-custom-suppliers',
  SuppliersConfigTabCustomFields = 'suppliers-config-tab-custom-fields',
  SuppliersConfigTabImport = 'suppliers-config-tab-import',
  SupplyChainContactsDialog = 'supply-chain-contact-dialog-grid',
  SupplyChainContactsDialogAddContact = 'supply-chain-contacts-dialog-add-contact',
  SupplyChainChartWrapper = 'supply-chain-chart-wrapper',
  SupplyChainNewViewButton = 'supply-chain-new-view-button',
  SupplyChainSaveViewNameButton = 'supply-chain-save-view-name-button',
  SupplyChainDeleteViewButton = 'supply-chain-delete-view-button',
  SupplyChainViewAddFilterButton = 'supply-chain-view-add-filter-button',
  SupplyChainViewRemoveFilterButton = 'supply-chain-view-remove-filter-button',
  SupplyChainViewResetFiltersButton = 'supply-chain-view-reset-filters-button',
  SupplyChainViewSubmitFiltersButton = 'supply-chain-view-submit-filters-button',
  SupplyChainViewSaveFiltersDropdown = 'supply-chain-view-save-filters-dropdown',
  SupplyChainViewSaveToCurrentButton = 'supply-chain-view-save-to-current-button',
  SupplyChainViewSaveToNewButton = 'supply-chain-view-save-to-new-button',
  SupplyChainViewDropdown = 'supply-chain-view-dropdown',
  SupplyChainManageViews = 'supply-chain-manage-views',
  SupplyChainNewChartButton = 'supply-chain-new-chart-button',
  SupplyChainChartEditButton = 'supply-chain-chart-edit-button',
  SupplyChainChartDeleteButton = 'supply-chain-chart-delete-button',
  SupplyChainChartEditChartDialog = 'supply-chain-chart-edit-chart-dialog',
  SupplyChainChartNewChartDialog = 'supply-chain-chart-new-chart-dialog',
  SupplyChainChartNewChartTypeInput = 'supply-chain-chart-new-chart-type-input',
  SupplyChainBarChartBar = 'supply-chain-bar-chart-bar',
  SupplyChainFunnelTooltipColumn = 'supply-chain-funnel-tooltip-column',
  SupplyChainSettingsSaveButton = 'supply-chain-settings-save-button',
  SupplyChainSettingsResetDefaultButton = 'supply-chain-settings-reset-default-button',
  SupplyChainDeleteCohortButton = 'supply-chain-delete-cohort-button',
  SupplyChainSelectSuppliersForCohortAutocomplete = 'supply-chain-select-suppliers-for-cohort-autocomplete',
  SupplyChainConfirmDeleteCohortButton = 'supply-chain-confirm-delete-cohort-button',
  SupplyChainSaveTaskConfig = 'save-task-config',
  SupplyChainTaskConfigName = 'task-config-name',
  SupplyChainTaskConfigEdit = 'task-config-edit',
  SupplyChainTaskConfigPreview = 'task-config-preview',
  SupplyChainTaskConfigList = 'task-config-list',
  SupplyChainTaskConfigClose = 'task-config-close',
  SuppliersPageAssignTasksButton = 'suppliers-assign-tasks',
  CompanyMatchingTableUnmatchedSortButton = 'company-matching-table-unmatched-sort-button',
  CompanyMatchingTableNoMatchFoundOption = 'company-matching-table-no-match-found-option',
  SupplyChainBulkDeploySelectContacts = 'bulk-deploy-select-contacts',
  SupplyChainBulkDeployAddNewContact = 'bulk-deploy-add-new-contact',
  SupplyChainBulkDeployAddContactsForm = 'bulk-deploy-add-contacts-form',
  SupplyChainBulkDeployAddAnotherTask = 'bulk-deploy-add-another-task',
  SupplyChainBulkDeployReportingYearDropdown = 'bulk-deploy-reporting-year-dropdown',
  SupplyChainBulkDeployEmailsTable = 'bulk-deploy-emails-table',
  SupplyChainScopeSummaryRow = 'scope-summary-row',
  SupplyChainEmissionsFactorEstimate = 'emissions-factor-estimate',
  SupplyChainTasksTabTable = 'supply-chain-tasks-tab-table',
  SupplyChainTasksViewResponseButton = 'supply-chain-tasks-view-response-button',
  SupplierTaskComment = 'supplier-task-comment',
  SupplyChainTaskSubmissionSuccessDialog = 'supply-chain-task-submission-success-dialog',
  SupplyChainReOpenTaskButton = 'supply-chain-re-open-task-button',
  SupplyChainImportFileLabel = 'supply-chain-import-file-label',
  SupplyChainCurrentEmissionsIntensityBlurb = 'supply-chain-current-emissions-intensity-blurb',
  SupplyChainAutoMappingButton = 'supply-chain-auto-mapping-button',
  SupplierMappingTableSearch = 'supplier-mapping-table-search',
  DeleteEmissionsFactorsButton = 'delete-emissions-factors-button',
  SupplierEmissionsTargetVsActualsChartEntryButton = 'supplier-emissions-target-vs-actuals-chart-entry-button',
  SupplierEmissionsBreakdownTable = 'supplier-emissions-breakdown-table',
  SupplierEmissionsVsTargetsBreakdownTable = 'supplier-emissions-vs-targets-breakdown-table',
  ViewFullDetailsTargetDialogButton = 'view-full-details-target-dialog-button',
  FullTargetDetailsDialog = 'full-target-details-dialog',
  SupplierEmissionsTargetsDialog = 'supplier-emissions-targets-dialog',
  SupplierPortalHeroSection = 'supplier-portal-hero-section',
  // #endregion

  // #region Finance
  AddButton = 'add-button',
  AddAssetButton = 'add-asset-button',
  AddAssetNextButton = 'add-asset-next-button',
  AddAssetNameField = 'add-asset-name-field',
  AssetCreateAssetGroupButton = 'asset-create-asset-group-button',
  AssetCreateAssetSovereignBondButton = 'asset-create-asset-sovereign-bond-button',
  AssetCreatePersonalMotorVehicleInsuranceButton = 'asset-create-asset-personal-motor-vehicle-insurance-button',
  AssetCreateCommercialRealEstateButton = 'asset-create-commercial-real-estate-button',
  AssetHeaderTitle = 'asset-header-title',
  PreviewSurveyButton = 'preview-survey-button',
  CreateSurveyButton = 'create-survey-button',
  DeleteAssetsButton = 'delete-assets-button',
  BulkImportNoFundColumn = 'bulk-import-no-fund-column',
  BulkImportNoAssetClassColumn = 'bulk-import-no-asset-class-column',
  BulkImportButton = 'bulk-import-button',
  BulkImportMatching = 'bulk-import-matching',
  BulkImportMatchingNoChangesList = 'bulk-import-matching-no-changes-list',
  BulkImportSchemaSelect = 'bulk-import-schema-select',
  FinanceImportErrorSnackbar = 'finance-import-error-snackbar',
  SandboxRealEstateButton = 'sandbox-real-estate-button',

  OpenFlagsIntroButton = 'open-flags-intro-button',
  CloseFlagsIntroButton = 'close-flags-intro-button',
  FinanceSnapshotChangelogDataGrid = 'finance-snapshot-changelog-data-grid',

  SurveyStateChip = 'survey-state-chip',
  EstimateSpinnerSnackbar = 'estimate-spinner-snackbar',
  EstimatingFootprintSnackbar = 'estimating-footprint-snackbar',
  CreatedHoldingWithEstimateSnackbar = 'created-holding-with-estimate-snackbar',
  CreatedHoldingSnackbar = 'created-holding-snackbar',
  FinanceMetricsBarSection = 'finance-metrics-bar-section',
  DownloadBulkImportTemplateButton = 'download-bulk-import-template-button',
  CreateAssetDialogContinueButton = 'create-asset-dialog-continue-button',
  AssetCorporatesDownloadTemplateButton = 'corporate-assets-download-template-button',
  AssetChangeLogTitle = 'FinanceChangelogTab-title',
  FinanceExploreDataButton = 'finance-explore-data-button',

  FinanceCreateSavedViewButton = 'finance-create-saved-view-button',
  FinanceEditSavedViewButton = 'finance-edit-saved-view-button',
  FinanceDeleteSavedViewButton = 'finance-delete-saved-view-button',
  FinanceSavedViewMetricSelector = 'finance-saved-view-metric-selector',
  FinanceSavedViewDimensionSelector = 'finance-saved-view-dimension-selector',
  FinanceSavedViewChartKindSelector = 'finance-saved-view-chart-kind-selector',
  FinanceSavedViewAddChartButton = 'finance-saved-view-add-chart-button',
  SavedViewsOnboardingDescription = 'saved-views-onboarding-description',
  // #endregion

  // #region Surveys
  StartSurveyButton = 'start-survey-button',
  SurveyStepContainer = 'survey-step-container',
  // #endregion

  // #region Public Schema
  ColumnSidePanelRequired = 'column-side-panel-required',
  ColumnSidePanelUniqueKey = 'column-side-panel-unique-key',
  ColumnSidePanelMetric = 'column-side-panel-metric',
  ColumnSidePanelIsCumulative = 'column-side-panel-is-cumulative',
  ColumnSidePanelDescription = 'column-side-panel-description',
  ColumnSidePanelUnitConfiguration = 'column-side-panel-unit-configuration',
  ColumnSidePanelCurrencyConfiguration = 'column-side-panel-currency-configuration',
  ColumnSidePanelValues = 'column-side-panel-values',

  // #endregion

  // #region Reductions
  PublishedPageSection = 'published-page-section',
  PlanTabSummary = 'plan-tab-summary',
  EmissionTargetChartWrapper = 'emission-target-chart-wrapper',
  SupplierEngagementTargetChart = 'supplier-engagement-target-chart',
  RenewableElectricityProcurementChart = 'renewable-electricity-procurement-chart',
  ShowInitiativesButton = 'show-initiatives-button',
  CommonInitiativesTable = 'common-initiatives-table',
  CarbonRemovalButton = 'carbon-removal-button',
  CarbonFundingTable = 'carbon-funding-table',
  PlanTabDropdownMenu = 'plan-tab-dropdown-menu',
  AddPlanButton = 'add-plan-button',
  DeletePlanButton = 'delete-plan-button',
  AddTargetButton = 'add-target-button',
  TargetGhgScopeCategorySelector = 'target-ghg-scope-category-selector',
  TargetIntensityType = 'target-intensity-type-selector',
  PlansOverviewButton = 'plans-overview-button',
  PlansOverviewSinglePlan = 'plans-overview-single-plan',
  ForecastFootprintSaveButton = 'forecast-footprint-save-button',
  // #endregion

  // #region Org Structure
  OrgStructureBlankSlate = 'org-structure-blank-slate',
  OrgStructureCreateNewVersionButton = 'org-structure-create-new-version-button',
  OrgStructureCreateNewVersionNameField = 'org-structure-create-new-version-name-field',
  OrgStructureDeleteVersionButton = 'org-structure-delete-version-button',
  OrgStructureCreateNewVersionSourceVersionIdField = 'org-structure-create-new-version-source-version-id-field',
  OrgStructureAddDataDropdown = 'org-structure-add-data-dropdown',
  OrgStructureAddOrgUnitTypeDropdownItem = 'org-structure-add-org-unit-type-dropdown-item',
  OrgStructureCreateNewOrgUnitTypeNameField = 'org-structure-create-new-org-unit-type-name-field',
  OrgStructureDeleteOrgUnitTypeButton = 'org-structure-delete-org-unit-type-button',
  OrgStructureCreateNewOrgUnitButton = 'org-structure-create-new-org-unit-button',
  OrgStructureCreateNewOrgUnitNameField = 'org-structure-create-new-org-unit-name-field',
  OrgStructureCreateNewOrgUnitDescriptionField = 'org-structure-create-new-org-unit-description-field',
  OrgStructureDeleteOrgUnitButton = 'org-structure-delete-org-unit-button',
  OrgStructureCreateNewOrgUnitTypeRelationshipButton = 'org-structure-create-new-org-unit-type-relationship-button',
  OrgStructureOrgUnitTypeRelationshipSelector = 'org-structure-org-unit-type-relationship-selector',
  OrgStructureOrgUnitRelationshipSelector = 'org-structure-org-unit-relationship-selector',
  OrgStructurePublishButton = 'org-structure-publish-button',
  OrgStructureDeleteButton = 'org-structure-delete-button',
  OrgStructureUnpublishButton = 'org-structure-unpublish-button',
  OrgStructureUnpublishAreYouSureSwitch = 'org-structure-unpublish-are-you-sure-switch',
  OrgStructureImportButton = 'org-structure-import-button',
  OrgStructureIsLegalEntity = 'org-structure-is-legal-entity',
  OrgStructureEffectiveFromDatePicker = 'org-structure-effective-from-date-picker',
  OrgStructureEffectiveToDatePicker = 'org-structure-effective-to-date-picker',
  // #endregion

  // #region Support
  SupportButton = 'support-button',
  SupportButtonDropdownMenu = 'support-button-dropdown-menu',
  SupportAddNewCaseButton = 'support-add-new-case-button',
  SupportCaseSubjectInput = 'support-case-subject-input',
  SupportCaseDescriptionInput = 'support-case-description-input',
  SupportCaseSubmitButton = 'support-case-submit-button',
  SupportCaseProductAreaSelect = 'support-case-product-area-select',
  SupportCasePrioritySelect = 'support-case-priority-select',
  SupportCaseCommentInput = 'support-case-comment-input',
  SupportCaseCommentSubmitButton = 'support-case-comment-submit-button',
  SupportCaseSubscribeButton = 'support-case-subscribe-button',
  SupportCaseCloseButton = 'support-case-close-button',
  SupportCaseSidebar = 'support-case-sidebar',
  SupportCaseTimeline = 'support-case-timeline',
  SupportCaseTimelineItem = 'support-case-timeline-item',
  SupportAttachmentButton = 'support-attachment-button',
  SupportAttachmentSubmitButton = 'support-attachment-submit-button',
  SupportCaseAttachmentDownloadButton = 'support-case-attachment-download-button',
  NavHelpButton = 'nav-help-button',
  SupportNewCaseMenuItem = 'support-new-case-menu-item',
  SupportCenterMenuItem = 'support-center-menu-item',
  SupportCaseList = 'support-case-list',
  SupportCaseListToolbarQuickSort = 'support-case-list-toolbar-quick-sort',
  SupportCaseListToolbarFilter = 'support-case-list-toolbar-filter',
  // #endregion

  // #region Auth
  AccountSettingsButton = 'account-settings-button',
  LoginWithPasswordButton = 'login-with-password-button',
  LoginWithMagicLinkButton = 'login-with-magic-link-button',
  LoginWithGoogleButton = 'login-with-google-button',
  LoginWithSsoButton = 'login-with-sso-button',
  SubmitEmailButton = 'submit-email-button',
  LoginButton = 'login-button',
  PasswordInput = 'password-input',
  UserBarButton = 'user-bar-button',
  OrganizationSettingsItem = 'organization-settings-item',
  OrganizationStructureItem = 'organization-structure-item',
  // #endregion

  // #region Measurement
  MeasurementProjectFilterSection = 'active-measurement-filter-section',
  ActiveMeasurementExpandButton = 'active-measurement-expand-button',
  DatasourceUploadButton = 'datasource-upload-button',
  DatasourceSidebarTaskOverviewTab = 'datasource-sidebar-task-overview-tab',
  DatasourceSidebarChangelogTab = 'datasource-sidebar-changelog-tab',
  DatasourceSidebarGuidanceTab = 'datasource-sidebar-guidance-tab',
  ConfirmFinishedUploadingButton = 'confirm-finished-uploading-button',
  DataFormatSelect = 'data-format-select',
  DataFormattingNotes = 'data-formatting-notes',
  FacilitiesAddDataButton = 'facilities-add-data-button',
  FacilitiesAddDataButtonBuildings = 'facilities-add-data-button-buildings',
  FacilitiesAddDataButtonUtilities = 'facilities-add-data-button-utilities',
  StandardUploadDataButton = 'standard-upload-data-button',
  UploadDropdownButton = 'upload-dropdown-button',
  UploadDirectEntryButton = 'upload-direct-entry-button',
  UploadOnboardingDialogConfirmation = 'upload-onboarding-dialog-confirmation',
  FacilitiesConfirmChangesButton = 'facilities-confirm-changes-button',
  ImportFlowSampleDataSchemaSelect = 'select-field-import-flow-sample-data',
  ImportFlowLeaveDialogSaveAndExitButton = 'import-flow-leave-dialog-save-and-exit-button',
  ImportFlowLeaveDialogDiscardFileButton = 'import-flow-leave-dialog-discard-file-button',
  ImporterV1IntroText = 'importer-v1-intro-text',
  CustomEmissionsFactorsGrid = 'custom-emissions-factors-grid',
  MeasurementBulkTaskCreationDropdown = 'measurement-bulk-task-creation-dropdown',
  MeasurementBulkTaskCreationButton = 'measurement-bulk-task-creation-button',
  MeasurementExportTaskListButton = 'measurement-export-task-list-button',
  BulkUploadTaskImporter = 'bulk-upload-task-importer',
  ImporterDialog = 'importer-dialog',
  ImporterSheet = 'importer-sheet',
  ImporterFileUploadStep = 'importer-file-upload-step',
  ImporterContinueButton = 'importer-continue-button',
  ImporterCloseButton = 'importer-close-button',
  EditFacilitiesAssignmentButton = 'edit-facilities-assignment-button',
  ConfirmFacilitiesAssignmentButton = 'confirm-facilities-assignment-button',
  FacilitiesGridExpandButton = 'facilities-grid-expand-button',
  FacilitiesGridEditButton = 'facilities-grid-edit-button',
  SingleFacilityPageSidebar = 'single-facility-page-sidebar',
  SingleFacilityPageCloseButton = 'single-facility-page-close-button',
  BuildingForm = 'building-form',
  FacilitiesTasksTab = 'facilities-tasks-tab',
  FacilitiesTasksToolbar = 'facilities-tasks-toolbar',
  UploadsTab = 'uploads-tab',
  DatasetsTab = 'datasets-tab',
  FacilitiesUtilitiesHeaderRowExpand = 'facilities-utilities-header-row-expand',
  FacilitiesExportDownloadAgainButton = 'facilities-export-download-again-button',
  OpenDirectEntryForFacility = 'open-direct-entry-for-facility',
  DirectEntrySubmitButton = 'direct-entry-submit-button',
  DirectEntryNextButton = 'direct-entry-next-button',
  BulkAssignTasksToUploadersAndApproversSaveButton = 'bulk-assign-tasks-to-uploaders-and-approvers-save-button',
  AssignUploadersDropdown = 'assign-uploaders-dropdown',
  AssignApproversDropdown = 'assign-approvers-dropdown',
  UserSelect = 'user-select',
  UseExistingOrCreateNewTaskRadioField = 'use-existing-or-create-new-task-radio-field',
  SubmitForApproval = 'submit-for-approval',
  BulkDeleteFacilities = 'bulk-delete-facilities',
  ApprovalsReopenTaskButton = 'approvals-reopen-task-button',
  TrackerGridGroupingColumnExpand = 'tracker-grid-grouping-column-expand',
  OpenNewDataIssueDialog = 'open-new-data-issue-dialog',
  NewDataIssueTitleField = 'new-data-issue-title-field',
  NewDataIssueMessageField = 'new-data-issue-message-field',
  DataIssueConversations = 'data-issue-conversations',
  DataIssueDialogTitleHeader = 'data-issue-dialog-title-header',
  DataIssueDialogMessage = 'data-issue-dialog-message',
  DataIssueResponseField = 'data-issue-response-field',
  ResolveDataIssueButton = 'resolve-data-issue-button',
  OpenDataIssuePanel = 'open-data-issue-panel',
  TaskStatusLabel = 'task-status-label',
  FileStatusText = 'file-status-text',
  FileStatusComponent = 'file-status-component',
  FilesListBuildingSupportingDocs = 'files-list-building-supporting-docs',
  FileName = 'file-name',
  EditSourceFileButton = 'edit-source-file-button',
  BiActivityDataEmbeddedChart = 'bi-activity-data-embedded-chart',
  BiActivityDataEmbeddedMetricSelector = 'bi-activity-data-embedded-metric-selector',
  BiActivityDataExploreButton = 'bi-activity-data-explore-button',
  AddDataDropdownOnFullPageDialog = 'add-data-dropdown-on-full-page-dialog',
  MeasurementProjectHeaderActionsDropdown = 'measurement-project-header-actions-dropdown',
  MeasurementProjectToggleStatusButton = 'measurement-project-toggle-project-status-button',
  MeasurementProjectClosedCallout = 'measurement-project-closed-callout',
  MeasurementProjectOverflowMenu = 'measurement-project-overflow-menu',
  MeasurementProjectCreateButton = 'measurement-project-create-button',
  MeasurementProjectNameCell = 'measurement-project-name-cell',
  MeasurementProjectHeaderControlsEditOrgStructure = 'measurement-project-header-controls-edit-org-structure',
  ProjectsListSidebarOrgStructure = 'projects-list-sidebar-org-structure',
  FlexibleProjectsTableSkeleton = 'flexible-projects-table-skeleton',
  FlexibleProjectNameCell = 'flexible-project-name-cell',
  FlexibleProjectsTable = 'flexible-projects-table',
  FlexibleProjectsTableBlankSlate = 'flexible-projects-table-blank-slate',
  FlexibleTasksTableBlankSlate = 'flexible-tasks-table-blank-slate',
  ProjectsPageActions = 'projects-page-actions',
  ProjectsListSidebar = 'projects-list-sidebar',
  // #endregion

  // #region Clean Power
  EacList = 'eac-list',
  EacFootprintsAllocatedTo = 'eac-footprints-allocated-to',
  // #endregion

  // #region Calculation Citations
  NoArchivedCitationsBlankSlate = 'no-archived-citations-blank-slate',
  ArchiveCitationButton = 'archive-citation-button',
  UnarchiveCitationButton = 'unarchive-citation-button',
  ArchivedCitationsObjectList = 'archived-citations-object-list',
  CitationConfigSectionArchive = 'citations-list-archive-link',
  EditCitationButton = 'edit-citation-button',
  // #endregion

  // #region Footprint
  BenchmarkGroupsPageSection = 'benchmark-groups-page-section',
  BenchmarksPageSection = 'benchmarks-page-section',
  NewVersionNoticeUpdateDescription = 'new-version-notice-update-description',
  NewVersionNoticeChip = 'new-version-notice-chip',
  NewVersionNoticeDialogButton = 'new-version-notice-dialog-button',
  NewVersionUpdatesInfoText = 'new-version-updates-info-text',
  NewVersionReviewUpdatesButton = 'new-version-review-updates-button',
  SaveRecategorizationRuleButton = 'save-recategorization-rule-button',
  ArchivedFootprintsObjectList = 'archived-footprints-object-list',
  UnarchiveFootprintButton = 'unarchive-footprint-button',
  NoArchivedFootprintsBlankSlate = 'no-archived-footprints-blank-slate',
  ArchivedFootprintNoPermissionDetailPage = 'archived-footprint-no-permission-detail-page',
  FacilitiesOccupancyInfoIcon = 'facilities-occupancy-info-icon',
  DataProcessingWarningChip = 'data-processing-warning-chip',
  MappingDataProcessingCallout = 'mapping-data-processing-callout',
  // #endregion
  // #region Overview
  EmissionsOverTimeSection = 'emissions-over-time-section',
  // #endregion

  // #region BI
  BiTreeDataGrid = 'bi-tree-data-grid',
  BiFlatDataGrid = 'bi-flat-data-grid',
  BiTableToolbar = 'bi-table-toolbar',
  BiChartContainer = 'bi-chart-container',
  BiTimeSeriesChart = 'bi-time-series-chart',
  BiBarChart = 'bi-bar-chart',
  BiPieChart = 'bi-pie-chart',
  BiMeterChart = 'bi-meter-chart',
  BiBigNumberChart = 'bi-big-number-chart',
  BiParetoChart = 'bi-pareto-chart',
  BiTreemapChart = 'bi-treemap-chart',
  BiErrorChart = 'bi-error-chart',
  BiEmptyChart = 'bi-empty-chart',
  BiActiveFiltersSection = 'bi-active-filters-section',
  BiActiveFilterForm = 'bi-filter-form',
  BiNoData = 'bi-no-data',
  BiActiveFilterDeleteButton = 'bi-active-filter-delete-button',
  BiActiveFiltersClearAll = 'bi-active-filters-clear-all',
  BiQueryControlPanelHeader = 'bi-query-control-panel-header',
  // #endregion

  // #region Drilldown
  DrilldownBarChart = 'drilldown-bar-chart',
  DrilldownPieChart = 'drilldown-pie-chart',
  DrilldownDatagridQuerySection = 'drilldown-datagrid-query-section',
  DrilldownDatagridQueryPanelAddFilterButton = 'drilldown-datagrid-query-panel-add-filter-button',
  DrilldownTableContent = 'drilldown-table-content',
  DrilldownGroupByAutocomplete = 'drilldown-group-by-autocomplete',
  MethodologyExplainerDialog = 'methodology-explainer-dialog',
  CalculationCitation = 'calculation-citation',
  ShowYourWorkButton = 'show-your-work-button',
  ShowYourWorkTable = 'show-your-work-table',
  DrilldownOverflowMenu = 'drilldown-overflow-menu',
  DrilldownConfigButton = 'drilldown-view-config-menu',
  // #endregion

  // #region Activity data
  ActivityTypeList = 'activity-type-list',
  ActivityTypeRow = 'activity-type-row',
  ActivityTypeDataTab = 'activity-type-data-tab',
  ActivityTypeUploadsTab = 'activity-type-uploads-tab',
  ActivityTypeDataIngestionTab = 'activity-type-data-ingestion-tab',
  ActivityTypeAnomaliesTab = 'activity-type-anomalies-tab',
  ActivityTypeRowCloudExpenses = 'activity-type-row-cloud_spend',
  ActivityTypeRowWaste = 'activity-type-row-waste',
  ActivityDataAddDataButton = 'activity-data-add-data-button',
  ActivityDataUploadsList = 'activity-data-uploads-list',
  // #endregion

  // #region Footprint configuration
  FootprintConfigSection = 'footprint-config-section',
  FootprintConfigSectionExclusion = 'footprints-list-exclusion-link',
  FootprintConfigSectionRecat = 'footprints-list-recat-link',
  FootprintConfigSectionExports = 'footprints-list-exports-link',
  FootprintConfigSectionArchive = 'footprints-list-archive-link',
  FootprintStatusPill = 'footprint-status-pill',
  FootprintExportStatusPill = 'footprint-export-status-pill',
  FootprintExportRegenerateButton = 'footprint-export-regenerate-button',
  FootprintExportDeleteButton = 'footprint-export-delete-button',
  FootprintConfigDrawerClose = 'footprint-config-drawer-close',
  CreateFootprintButton = 'create-footprint-button',
  CreateNewFootprintButton = 'create-new-footprint-button',
  EditFootprintButton = 'edit-footprint-button',
  ArchiveFootprintButton = 'archive-footprint-button',
  LockedEditFootprintButton = 'locked-footprint-button',
  EditFootprintNameButton = 'edit-footprint-name-button',
  DrilldownFootprintButton = 'drilldown-footprint--button',
  FootprintListPageSidebarLearningHubLink = 'footprint-list-page-sidebar-learning-hub-link',
  FootprintListPageSidebarHowToCreateFootprintLink = 'footprint-list-page-sidebar-how-to-create-footprint-link',
  FootprintListPageSidebarMethodologyUpdatesLink = 'footprint-list-page-sidebar-methodology-updates-link',
  FootprintAssemblyDatasetsStepLoading = 'footprint-assembly-datasets-step-loading',
  NoCleanPowerApplied = 'no-clean-power-applied',
  CleanPowerApplied = 'clean-power-applied',
  // #endregion

  // #region Categorization and Exclusion rules
  CreateNewProductRuleButton = 'create-new-product-rule-button', // used for both recat and exclusion rules
  DeduplicationPage = 'deduplication-page',
  ExclusionIndexView = 'exclusion-index-view',
  DeduplicationRulePage = 'deduplication-rule-page',
  DeduplicationPreviewGrid = 'deduplication-preview-grid',
  ExclusionRulesDetailEmptyState = 'exclusion-rules-detail-empty-state',
  SaveDeduplicationRuleButton = 'save-deduplication-rule-button',
  AddExclusionFilterCondition = 'add-exclusion-filter-condition',
  ExclusionRuleBlock = 'exclusion-rule-block',
  ExclusionRuleBlockLoading = 'exclusion-rule-block-loading',
  ExclusionRuleBlockReadOnlyTooltip = 'exclusion-rule-block-read-only-tooltip',
  RecategorizationPreviewGrid = 'recategorization-preview-grid',
  CategoryRuleBlock = 'category-rule-block',
  CategoryRuleBlockLoading = 'category-rule-block-loading',
  CategoryRuleBlockReadOnlyTooltip = 'category-rule-block-read-only-tooltip',
  // #endregion

  // #region Data lineage
  DataLineageViewModeToggle = 'data-lineage-view-mode-toggle',
  TransformStepsContainer = 'transform-steps-container',
  TransformContent = 'transform-content',
  // #endregion

  // #region Footprint selectors
  FootprintSelector = 'footprint-selector',
  FootprintSelectorLoading = 'footprint-selector-loading',
  FootprintOnlySelector = 'footprint-only-selector',
  FootprintWithSnapshotSelector = 'footprint-with-snapshot-selector',
  NoPermissionFootprintDisplay = 'no-permission-footprint-display',
  // #endregion

  // #region Footprint detail
  FootprintDetailPageLegacyWarning = 'footprint-detail-page-legacy-warning',
  FootprintDetailMoreActionsButton = 'footprint-detail-more-actions-button',
  FootprintDetailReviewOrApproveDropdown = 'footprint-detail-review-or-approve-dropdown',
  FootprintDetailVersionHistoryButton = 'footprint-detail-version-history-button',
  FootprintDetailShell = 'footprint-detail-shell',
  FootprintDetailBlankSlate = 'footprint-detail-blank-slate',
  FootprintDetailPageActionsContainer = 'footprint-detail-page-actions-container',
  // #endregion

  // #region Custom activity + EFs
  ConfirmReplaceEmissionsFactors = 'confirm-replace-emissions-factors',
  // #endregion

  // #region Reporting
  BenchmarksCard = 'benchmarks-card',
  ScienceInfoCard = 'science-info-card',
  TcfdOnboardingDialog = 'tcfd-onboarding-dialog',
  CreateReportButton = 'create-report-button',
  GoToReportButton = 'go-to-report-button',
  AddOpportunityButton = 'add-opportunity-button',
  ReportQuestionDataGrid = 'report-question-data-grid',
  ReportingFlexibleReportBiQueryDialogButton = 'reporting-flexible-report-bi-query-button',
  ReportingFlexibleReportBiQueryDialogHeader = 'reporting-flexible-report-bi-query-header',
  ReportingFlexibleReportBiQueryDialogError = 'reporting-flexible-report-bi-query-error-callout',
  CreateFlexibleReportBiResponseButton = 'create-flexible-report-bi-response-button',
  // #endregion

  // #region Viz
  CategoricalBarChart = 'categorical-bar-chart',
  // #endregion

  // #region General
  FileUploadInput = 'file-upload-input',
  CloseDialogButton = 'close-dialog-button',
  DialogFormSubmitButton = 'dialog-form-submit-button',
  DialogConfirmSubmitButton = 'dialog-confirm-submit-button',
  CollapsibleSectionButton = 'collapsible-section-button',
  DropdownMenuButton = 'dropdown-menu-button',
  Sidebar = 'sidebar',
  SidebarUserBar = 'sidebar-user-bar',
  LogoutButton = 'logout-button',
  Breadcrumb = 'breadcrumb',
  BreadcrumbSeriesFirstCrumb = 'breadcrumb-series-first-crumb',
  BreadcrumbSeriesLastCrumb = 'breadcrumb-series-last-crumb',
  AutocompleteCount = 'autocomplete-count',
  FilterRowFieldAutocomplete = 'filter-row-field-autocomplete',
  FilterRowValueAutocomplete = 'filter-row-value-autocomplete',
  FilterRowValueChip = 'filter-row-value-chip',
  FilterRowDeleteButton = 'filter-row-delete-button',
  ResetButton = 'reset-button',
  SaveButton = 'save-button',
  SidebarSubNavAddEntryButton = 'sidebar-sub-nav-add-entry-button',
  AdminBar = 'admin-bar',
  SwitchOrgOption = 'switch-org-option',
  // #endregion

  // #region Home
  HomeWelcomeCard = 'home-welcome-card',
  // #endregion

  // #region ui-core
  CircularProgressDeterminate = 'circular-progress-determinate',
  FileUploadMultipleImport = 'file-upload-multiple-import',
  FileUploadMultipleCancel = 'file-upload-multiple-cancel',
  FileUploadMultipleDelete = 'file-upload-multiple-delete',
  FileUploadMultipleDownload = 'file-upload-multiple-download',
  MarkdownTextFieldWithPreviewDisplay = 'markdown-text-field-with-preview-display',
  MarkdownTextFieldWithPreviewTextArea = 'markdown-text-field-with-preview-textarea',
  MarkdownTextFieldWithPreviewToggle = 'markdown-text-field-with-preview-toggle',
  SheetNavButtonFinish = 'sheet-nav-button-finish',
  SheetButtonClose = 'sheet-button-close',
  SheetButtonHelp = 'sheet-button-help',
  TextFieldInlineEdit = 'text-field-inline-edit',
  TextFieldInlineEditButton = 'text-field-inline-edit-button',
  ItemSelectionCard = 'item-selection-card',
  YMIntervalPopoverButton = 'ym-interval-popover-button',
  YMIntervalPopover = 'ym-interval-popover',
  OverflowActionButton = 'overflow-action-button',
  Checkbox = 'checkbox',
  DataGridDrawerCollapseButton = 'data-grid-drawer-collapse-button',
  DataGridDrawerSearchButton = 'data-grid-drawer-search-button',
  DataGridDrawerColumnsButton = 'data-grid-drawer-columns-button',
  DataGridDrawerFilterButton = 'data-grid-drawer-filter-button',
  // #endregion

  // #region Error states
  ErrorStateFullPage = 'error:page',
  ErrorStateSnackbar = 'error:snackbar',
  ErrorStateCallout = 'error:callout',
  // #endregion

  // #region loading states
  LoadingStateSkeleton = 'loading:skeleton',
  LoadingStateCircularProgress = 'loading:circular-progress',
  LoadingStateBigLoading = 'loading:big-loading',
  LoadingStateDataGrid = 'loading:data-grid',
  LoadingStateDelayedCircular = 'loading:delayed-circular-pro',
  // #endregion

  // #region Org settings
  CreateRoleButton = 'create-role-button',
  RoleNameInput = 'role-name-input',
  RoleDescriptionInput = 'role-description-input',
  InviteUserButton = 'invite-user-button',
  AddUsersLink = 'add-users-link',
  UserChip = 'user-chip',
  UserNameInput = 'user-name-input',
  UserEmailInput = 'user-email-input',
  UserEmailDomainSelector = 'user-email-domain-selector',
  PermissionCheckboxPrefix = 'permission-checkbox',
  PermissionSwitchPrefix = 'permission-switch',
  PermissionsSidebarSection = 'permissions-sidebar-section',
  PermissionsAvatars = 'permissions-avatars',

  // v2 test ids
  RoleNavigationItem = 'role-navigation-item',
  UsersNavigationItem = 'users-navigation-item',
  PermissionsNavigationItem = 'permissions-navigation-item',
  PreferencesNavigationItem = 'preferences-navigation-item',
  AccessAndSecurityNavigationItem = 'access-and-security-navigation-item',
  IntegrationsNavigationItem = 'integrations-navigation-item',
  ApiNavigationItem = 'api-navigation-item',
  InPageNavigationItem = 'in-page-navigation-item',
  SettingsCommandButton = 'settings-command-button',
  RoleDeleteButton = 'role-delete-button',
  UsersChipListOverflowButton = 'users-chip-list-overflow-button',

  ApiSettingsDialogNameField = 'api-settings-dialog-name-field',
  ApiSettingsDialogDescriptionField = 'api-settings-dialog-description-field',
  ApiSettingsDialogRolesField = 'api-settings-dialog-roles-field',
  ApiSettingsDialogCreateButton = 'api-settings-dialog-create-button',
  ApiSettingsDialogSecretField = 'api-settings-dialog-secret-field',
  ApiSettingsDialogDoneButton = 'api-settings-dialog-done-button',
  CreateApiKeyButton = 'create-api-key-button',
  ApiOauthClientGrid = 'api-oauth-client-grid',
  ApiAuthMethodDropdown = 'api-auth-method-dropdown',
  ApiAuthMethodDropdownTokenTab = 'api-auth-method-dropdown-token-tab',
  ApiAuthMethodDropdownOAuthTab = 'api-auth-method-dropdown-oauth-tab',
  // #endregion

  // #region Audit Log
  AuditLogsGrid = 'audit-logs-grid',
  AuditLogFilterButton = 'audit-log-filter-button',
  AuditLogSortButton = 'audit-log-sort-button',
  AuditLogDateIntervalButton = 'audit-log-date-interval-button',
  AuditLogExportButton = 'audit-log-export-button',
  AuditLogEntrySidePanel = 'audit-log-entry-side-panel',
  AuditLogFilterSection = 'audit-log-filter-section',
  // #endregion

  // #region AdminAuth
  AdminRequestAccessFormAccessWarning = 'admin-request-access-form-access-warning',
  AdminRequestAccessFormAccessDuration = 'admin-request-access-form-access-duration',
  ContractorRequestAccessFormAccessWarning = 'contractor-request-access-form-access-warning',
  ContractorRequestAccessFormAccessDuration = 'contractor-request-access-form-access-duration',
  // #endregion

  // #region Admin
  OrganizationPageOrganizationTypeDropdown = 'organization-page-organization-type-dropdown',
  // #endregion

  // #region Forbidden Page
  ForbiddenPage = 'forbidden-page',
  ForbiddenPageBody = 'forbidden-page-body',
  ForbiddenIcon = 'forbidden-icon',
  // #endregion

  // #region Footprint Exports
  ExportNotFound = 'export-not-found',
  // #endregion

  // #region NotFound Page
  NotFoundPage = 'not-found-page',
  NotFoundPageBody = 'not-found-page-body',
  NotFoundIcon = 'not-found-icon',
  // #endregion

  // #region Unexpected Error
  UnexpectedErrorPageBody = 'unexpected-error-body',
  // #endregion

  // #region  Error
  ErrorPageBody = 'error-body',
  ErrorWarningIcon = 'warning-icon',
  ErrorErrorIcon = 'error-icon',
  ErrorSyncIcon = 'sync-icon',
  ErrorSupportLink = 'support-link',
  // #endregion

  // #region Value mappings
  ValueMappingsTable = 'value-mappings-table',
  ValueMappingsNoRows = 'value-mappings-no-rows',
  ValueMappingsRowInfoButton = 'value-mappings-row-info-button',
  ValueMappingsRowAutocomplete = 'value-mappings-row-autocomplete',
  ValueMappingsSidebarTabChangelog = 'value-mappings-sidebar-tab-changelog',
  ValueMappingsSidebarTabFiles = 'value-mappings-sidebar-tab-files',
  ValueMappingsSidebarFileRow = 'value-mappings-sidebar-file-row',
  ValueMappingsTaskRow = 'value-mappings-task-row',
  ValueMappingsApproveAllButton = 'value-mappings-approve-all-button',
  // #endregion

  AITrigger = 'ai-trigger',
  AIChatSidebar = 'ai-chat-sidebar',
  AICloseButton = 'ai-close-button',
  AIResetButton = 'ai-reset-button',
  AIRetryButton = 'ai-retry-button',
  AISubmitButton = 'ai-submit-button',
  AIStopButton = 'ai-stop-button',
  AIUserMessage = 'ai-user-message',
  AIAssistantMessage = 'ai-assistant-message',
  AIHistoryButton = 'ai-history-button',
  AINewConversationButton = 'ai-new-conversation-button',

  // #region Reference Table Upload
  ReferenceTableUploadFlowNextButton = 'reference-table-upload-flow-next-button',
  ReferenceTableUploadFlowSaveButton = 'reference-table-upload-flow-save-button',
  ReferenceTableUploadFlowStepNavigator = 'reference-table-upload-flow-step-navigator',
  ReferenceTableUploadFlowConfigureStep = 'reference-table-upload-flow-configure-step',
  ReferenceTableUploadFlowUploadStep = 'reference-table-upload-flow-upload-step',
  // #endregion

  // #region Email
  CtaButton = 'cta-button',
  EmailLink = 'email-link',
  ImageButton = 'image-button',
  // #endregion

  // #region Calculation Customization
  // Calculation methods
  CalculationMethodsList = 'calculation-methods-list',
  CalculationMethodDescription = 'calculation-method-description',
  CalculationMethodPreviewPanelCloseButton = 'calculation-method-preview-panel-close-button',
  CalculationMethodDetailResizableDrawer = 'calculation-method-detail-resizable-drawer',
  PreviewCustomizationButton = 'preview-customization-button',
  EditOrCreateCustomizationButton = 'edit-or-create-customization-button',
  RefExpressionBuilderSyntaxMode = 'ref-expression-builder-syntax-mode',
  RefExpressionBuilderFormMode = 'ref-expression-builder-form-mode',
  CustomizationSelector = 'customization-selector',
  // Reference tables
  ReferenceTablesList = 'reference-tables-list',
  ReferenceTableDetailBlankSlate = 'reference-table-detail-blank-slate',
  ReferenceTableDetailMetadata = 'reference-table-detail-metadata',
  CreateReferenceTableButton = 'create-reference-table-button',
  EditReferenceTableButton = 'edit-reference-table-button',
  ReferenceTableVersionHistoryButton = 'reference-table-version-history-button',
  // Citations
  CitationsList = 'citations-list',
  CitationDetailMainCard = 'citation-detail-main-card',
  CitationDetailSidebarCard = 'citation-detail-sidebar-card',
  CreateCitationButton = 'create-citation-button',
  // Customizations
  CustomizationsList = 'customizations-list',
  EditNameAndDescriptionCustomizationButton = 'edit-name-and-description-customization-button',
  ArchiveCustomizationButton = 'archive-customization-button',
  EditCustomizationButton = 'edit-customization-button',
  // #endregion

  // #region Custom Activity Types
  RequiredColumnSwitch = 'switch-required-column',
  IsAdditiveSwitch = 'switch-is-additive',
  IsMetricSwitch = 'switch-is-metric',
  UnitInput = 'unit-input',
  AddUnitButton = 'add-unit-button',
  UnitListPopover = 'unit-list-popover',
  UnitListOverflowButton = 'unit-list-overflow-button',
  EnumColumnAddOptionButton = 'add-enum-value-button',
  QuickAddLength = 'quick-add-length',
  QuickAddArea = 'quick-add-area',
  QuickAddEnergy = 'quick-add-energy',
  ClearAllSelectedUnits = 'clear-all-selected-units',
  SelectedUnitChips = 'selected-unit-chips',

  // #endregion

  // #region Product Footprints
  MaterialsDataGrid = 'materials-data-grid',
  UploadMaterialsButton = 'upload-materials-button',
  UploadSupplierLcasButton = 'upload-supplier-lcas-button',
  SaveMaterialMethodologyVersionButton = 'save-material-methodology-version-button',
  ViewMaterialMethodologyVersionsButton = 'view-material-methodology-versions-button',
  SupplierLcaUploadTemplateSelectionPage = 'supplier-lca-upload-template-selection-page',
  SupplierLcaUploadReviewSubmitButton = 'supplier-lca-upload-review-submit-button',
  MoreMaterialLibraryActionsButton = 'more-material-library-actions-button',
  BulkDeleteMaterialsButton = 'bulk-delete-materials-button',
  GenerateProductFootprintsButton = 'generate-product-footprints-button',
  MaterialAutoMappingCommand = 'material-auto-mapping',
  MaterialActionsButton = 'material-actions-button',
  ProductFootprintDataExplorerButton = 'product-footprint-data-explorer-button',
  ProductFootprintCompareButton = 'product-footprint-compare-button',
  PfBiMaterialMethodologyVersionSelector = 'product-footprint-bi-mmv-selector',
  PfBiViewModeSelector = 'product-footprint-bi-view-mode-selector',
  PfProductionGraphEditViewButton = 'pf-production-graph-edit-view-button',
  PfProductionGraphEditor = 'production-graph-editor',
  PfProductionGraphEditorHeader = 'pf-production-graph-editor-header',
  PfProductionGraphNodeEditForm = 'pf-production-graph-node-edit-form',
  RunModelMenuItem = 'run-model-menu-item',
  RunProxyMenuItem = 'run-proxy-menu-item',
  RunFlashMenuItem = 'run-flash-menu-item',
  ChangeSelectedPcfItem = 'change-selected-pcf-item',
  SelectFromMaterialLibraryMenuItem = 'select-from-material-library-menu-item',
  EditMaterialDetailsMenuItem = 'edit-material-details-menu-item',
  ViewProductionGraphButton = 'view-production-graph-button',
  PfLifecycleAssessmentsTable = 'pf-lifecycle-assessments-table',
  PfSearchMaterialsButton = 'pf-search-materials-button',
  PfSearchMaterialsDialog = 'pf-search-materials-dialog',
  PfMaterialSearchListTable = 'pf-material-search-list-table',

  PurchaseOrderMappingTaskRow = 'purchase-order-mapping-task-row',
  PurchaseOrderMappingTable = 'purchase-order-mapping-table',
  PurchaseOrderMappingReRunButton = 'purchase-order-mapping-re-run-button',
  // #endregion

  LoadingPage = 'loading-page',
}
export enum TestIdPrefix {
  Checkbox = 'checkbox',
  CheckboxField = 'checkboxField',
  autocompleteField = 'autocompleteField',
  ZodField = 'ZodField',
  ZodFieldAutocompletePopper = 'ZodFieldAutocompletePopper',
  ZodFormSubmit = 'ZodForm-submit',
  BiActivityDataEmbeddedMetricOption = 'bi-activity-data-embedded-metric-option',
}

export function getPrefixedTestId(prefix: TestIdPrefix, id: string): string {
  return `${prefix}-${id}`;
}

interface MockReplaceProps {
  name: string;
  excludedKeys?: Array<string>;
}

function replaceMockWithActual({
  name,
  jest: actualJest,
  excludedKeys = [],
}: MockReplaceProps & {
  jest: Pick<typeof jest, 'requireMock' | 'requireActual'>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mock = actualJest.requireMock(name) as any;
  const required = actualJest.requireActual(name);
  const allKeys = Object.keys(required);
  const keysNotInModule = difference(excludedKeys, allKeys);
  invariant(
    keysNotInModule.length === 0,
    `Tried to exclude unknown export from module ${name} - ${keysNotInModule}`
  );
  Object.assign(mock, omit(required, excludedKeys));
  return mock;
}

/**
   Replaces an array of jest mocks with the actual implementation. You can specify keys to leave mocked via excludedKeys.
   You must supply the global jest object from the test file in which the mocking was done.
   @example
   ```ts
      jest.mock('../FilesService');
      jest.mock('@watershed/service-essentials/utils/gcp');
      jest.mock('@watershed/service-essentials/utils/GcsClient', () => ({
        GcsClient: {
          create: jest.fn().mockReturnValue({
            copyFile: jest.fn(),
          }),
        },
      }));
      replaceMocksWithActual(
        jest,
        {
          name: '../FilesService',
          excludedKeys: ['uploadToGcsWithFileMetadata'],
        },
        '@watershed/service-essentials/utils/gcp',
        {
          name: '@watershed/service-essentials/utils/GcsClient',
          excludedKeys: ['GcsClient'],
        }
      );
   ```
 */

export function replaceMocksWithActual(
  actualJest: Pick<typeof jest, 'requireMock' | 'requireActual'>,
  ...mocks: Array<string | MockReplaceProps>
): void {
  mocks.map((name) => {
    const mockReplaceProps = typeof name === 'string' ? { name } : name;
    replaceMockWithActual({ ...mockReplaceProps, jest: actualJest });
  });
}

/**
 * Shallow expect all fields in an object to be equal,
 * omitting some fields.
 */
export function expectObjectEqualityOmitFields(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  testObject: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expectObject: Record<string, any>,
  omitFields: Array<string>
): void {
  expect(omit(testObject, omitFields)).toMatchObject(
    omit(expectObject, omitFields)
  );
}

export function expectToBeDefined<T>(
  value: T | null | undefined
): asserts value is T {
  expect(value).toBeDefined();
  expect(value).not.toBeNull();
}

export function sortById<T extends { id: string }>(arr: Array<T>): Array<T> {
  return sortBy(arr, (element) => element.id);
}
